import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="accordion-toggle"
export default class extends Controller {
  static targets = ["button", "link"];

  connect() {
    this.accordionEl = this.element.querySelector(
      '[data-controller~="accordion"]'
    );
    if (!this.accordionEl) return;

    this._onOpen = () => this._syncSoon();
    this._onClose = () => this._syncSoon();
    this.accordionEl.addEventListener("accordion:open", this._onOpen);
    this.accordionEl.addEventListener("accordion:close", this._onClose);

    // Set ARIA attributes on button and links
    this.setAriaAttributes();
    this.updateButtonText();
  }

  disconnect() {
    if (!this.accordionEl) return;
    this.accordionEl.removeEventListener("accordion:open", this._onOpen);
    this.accordionEl.removeEventListener("accordion:close", this._onClose);
  }

  toggleAll() {
    if (!this.accordionEl) return;
    if (!this.hasButtonTarget) return;

    const expand = this.buttonTarget.textContent === "Expand All";

    if (expand) {
      this.linkTargets.forEach((link, idx) => {
        if (!link.classList.contains("st-accordion__icon--opened")) {
          link.click();
          link.setAttribute("aria-expanded", "true");
          const region = document.getElementById(link.getAttribute("aria-controls"));
          if (region) region.setAttribute("aria-hidden", "false");
        }
      });
    } else {
      this.linkTargets.forEach((link, idx) => {
        if (link.classList.contains("st-accordion__icon--opened")) {
          link.click();
          link.setAttribute("aria-expanded", "false");
          const region = document.getElementById(link.getAttribute("aria-controls"));
          if (region) region.setAttribute("aria-hidden", "true");
        }
      });
    }

    // update instantly (before animation finishes)
    this.updateButtonText();
  }
  setAriaAttributes() {
    // Set ARIA attributes for the expand/collapse all button
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("role", "button");
      this.buttonTarget.setAttribute("aria-expanded", this.allExpanded() ? "true" : "false");
      this.buttonTarget.setAttribute("aria-controls", this.linkTargets.map((l, i) => l.getAttribute("aria-controls")).join(" "));
      this.buttonTarget.setAttribute("tabindex", "0");
    }
    // Set ARIA attributes for each accordion link and region
    this.linkTargets.forEach((link, idx) => {
      const regionId = link.getAttribute("aria-controls") || `accordion-section-${idx}`;
      link.setAttribute("role", "button");
      link.setAttribute("aria-expanded", link.classList.contains("st-accordion__icon--opened") ? "true" : "false");
      link.setAttribute("aria-controls", regionId);
      link.setAttribute("tabindex", "0");
      // Find or create the region
      let region = document.getElementById(regionId);
      if (!region) {
        region = document.createElement("div");
        region.id = regionId;
        link.parentNode.insertBefore(region, link.nextSibling);
      }
      region.setAttribute("role", "region");
      region.setAttribute("aria-hidden", link.classList.contains("st-accordion__icon--opened") ? "false" : "true");
    });
  }

  // --- State helpers ---
  get itemCount() {
    return this.linkTargets.length;
  }

  get openCount() {
    return this.linkTargets.filter((link) =>
      link.classList.contains("st-accordion__icon--opened")
    ).length;
  }

  allCollapsed() {
    return this.openCount === 0;
  }

  allExpanded() {
    return this.itemCount > 0 && this.openCount === this.itemCount;
  }

  updateButtonText() {
    if (!this.hasButtonTarget) return;

    if (this.allExpanded()) {
      this.buttonTarget.textContent = "Collapse All";
      this.buttonTarget.setAttribute("aria-expanded", "true");
    } else {
      this.buttonTarget.textContent = "Expand All";
      this.buttonTarget.setAttribute("aria-expanded", "false");
    }
  }

  _syncSoon() {
    if (window.queueMicrotask) {
      queueMicrotask(() => this.updateButtonText());
    } else {
      setTimeout(() => this.updateButtonText(), 0);
    }
  }
}
